import { useState } from "react";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

export default function BMICalculator() {

  const [weight, setWeight] =
    useState("");

  const [height, setHeight] =
    useState("");

  const [bmi, setBmi] =
    useState(null);

  const [category, setCategory] =
    useState("");

  const [chartData, setChartData] =
    useState([]);

  const [error, setError] =
    useState("");

  const calculateBMI = () => {

    setError("");

    if (
      !weight ||
      !height ||
      weight <= 0 ||
      height <= 0
    ) {

      setBmi(null);

      setError(
        "Please enter valid weight and height"
      );

      return;
    }

    const h = height / 100;

    const result =
      weight / (h * h);

    const finalBMI =
      result.toFixed(2);

    setBmi(finalBMI);

    let bmiCategory =
      "";

    if (result < 18.5) {
      bmiCategory =
        "Underweight";
    }

    else if (result < 25) {
      bmiCategory =
        "Normal";
    }

    else if (result < 30) {
      bmiCategory =
        "Overweight";
    }

    else {
      bmiCategory =
        "Obesity";
    }

    setCategory(
      bmiCategory
    );

    setChartData([
      {
        name: bmiCategory,
        value: result,
      },
      {
        name: "Remaining",
        value: 40 - result,
      },
    ]);
  };

  const COLORS =
    ["#2563eb", "#e5e7eb"];

  return (
    <>
      <SEO
        title="BMI Calculator Online - Check Body Mass Index"
        description="Use our free BMI Calculator to check Body Mass Index instantly. Find out whether you are underweight, normal, overweight, or obese."
        keywords="bmi calculator, body mass index calculator, health calculator, obesity calculator"
        url="https://online-tools-center.vercel.app/bmi-calculator"
      />

      <Navbar />

      <div className="container py-4">

        <div className="row justify-content-center">

          <div className="col-lg-9">

            <div className="card shadow-lg border-0 rounded-4 p-4">

              {/* HERO */}

              <div className="text-center mb-4">

                <h1 className="fw-bold mb-3">
                  BMI Calculator
                </h1>

                <p className="text-muted">
                  Calculate your Body Mass Index
                  instantly and check whether you
                  are underweight, normal,
                  overweight, or obese.
                </p>

                <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">

                  <span className="badge bg-light text-dark p-2">
                    ✓ Instant BMI Result
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Health Category
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Free Tool
                  </span>

                  <span className="badge bg-light text-dark p-2">
                    ✓ Mobile Friendly
                  </span>

                </div>

              </div>

              <div className="row g-4 align-items-center">

                {/* LEFT */}

                <div className="col-md-6">

                  <label className="fw-semibold mb-2">
                    Weight (kg)
                  </label>

                  <input
                    type="number"
                    placeholder="Enter Weight"
                    className="form-control mb-3"
                    value={weight}
                    onChange={(e) =>
                      setWeight(e.target.value)
                    }
                  />

                  <label className="fw-semibold mb-2">
                    Height (cm)
                  </label>

                  <input
                    type="number"
                    placeholder="Enter Height"
                    className="form-control mb-3"
                    value={height}
                    onChange={(e) =>
                      setHeight(e.target.value)
                    }
                  />

                  <button
                    className="btn btn-primary w-100 py-2 fw-semibold"
                    onClick={calculateBMI}
                  >
                    Calculate BMI
                  </button>

                  {error && (
                    <div className="alert alert-danger mt-3">
                      {error}
                    </div>
                  )}

                  {bmi && (
                    <div className="alert alert-success mt-4">

                      <h5 className="mb-2">
                        Your BMI
                      </h5>

                      <h2 className="fw-bold">
                        {bmi}
                      </h2>

                      <p className="mb-0">
                        Category:
                        <strong>
                          {" "}
                          {category}
                        </strong>
                      </p>

                    </div>
                  )}

                </div>

                {/* RIGHT */}

                <div className="col-md-6">

                  <div className="bg-light rounded-4 p-4">

                    <h4 className="fw-bold mb-3">
                      BMI Categories
                    </h4>

                    <ul>

                      <li>
                        Underweight:
                        Below 18.5
                      </li>

                      <li>
                        Normal:
                        18.5 – 24.9
                      </li>

                      <li>
                        Overweight:
                        25 – 29.9
                      </li>

                      <li>
                        Obesity:
                        30 and above
                      </li>

                    </ul>

                    {bmi && (
                      <div
                        style={{
                          width: "100%",
                          height: 250,
                        }}
                      >

                        <ResponsiveContainer>

                          <PieChart>

                            <Pie
                              data={chartData}
                              dataKey="value"
                              outerRadius={80}
                              label
                            >

                              {chartData.map(
                                (
                                  entry,
                                  index
                                ) => (

                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      COLORS[index]
                                    }
                                  />

                                )
                              )}

                            </Pie>

                            <Tooltip />

                            <Legend />

                          </PieChart>

                        </ResponsiveContainer>

                      </div>
                    )}

                  </div>

                </div>

              </div>

            </div>

          {/* SEO CONTENT */}
<div className="mt-5">

  <h2 className="fw-bold mb-3">What is BMI?</h2>
  <p>
    BMI, or Body Mass Index, is a numerical value calculated from a person's
    weight and height. It is widely used by doctors, fitness professionals, and
    health organizations as a quick screening tool to classify individuals into
    weight categories that may indicate potential health risks. While BMI does
    not directly measure body fat, research has shown that BMI correlates
    reasonably well with body fat percentage in most adults and is a practical
    first indicator of whether someone may be underweight, at a healthy weight,
    overweight, or obese.
  </p>

  <h2 className="fw-bold mt-5 mb-3">BMI Formula</h2>
  <p>
    BMI is calculated using the formula: BMI = Weight (kg) ÷ Height² (m²).
    For example, if you weigh 70 kg and your height is 175 cm (1.75 m), your
    BMI is 70 ÷ (1.75 × 1.75) = 70 ÷ 3.0625 = 22.86. This would place you
    in the Normal weight category. Our calculator handles this formula
    automatically — you simply enter your weight in kilograms and your height
    in centimeters and the result appears instantly.
  </p>

  <h2 className="fw-bold mt-5 mb-3">How to Use This BMI Calculator</h2>
  <ol>
    <li className="mb-2">
      <strong>Enter your weight</strong> in kilograms in the first input field.
      If you know your weight in pounds, divide by 2.205 to convert to kg.
    </li>
    <li className="mb-2">
      <strong>Enter your height</strong> in centimeters. If you know your
      height in feet and inches, multiply feet by 30.48 and add inches
      multiplied by 2.54.
    </li>
    <li className="mb-2">
      <strong>Click Calculate BMI</strong> — your BMI value and weight
      category appear instantly along with a visual chart.
    </li>
    <li className="mb-2">
      <strong>Review your category</strong> and use the BMI ranges below to
      understand what your result means for your health.
    </li>
  </ol>

  <h2 className="fw-bold mt-5 mb-3">BMI Categories Explained</h2>
  <p>
    The World Health Organization (WHO) defines four standard BMI categories
    for adults. A BMI below 18.5 is classified as Underweight, which may
    indicate malnutrition, an eating disorder, or other health conditions that
    need medical attention. A BMI between 18.5 and 24.9 is considered Normal
    weight, which is associated with the lowest risk of weight-related health
    problems. A BMI between 25 and 29.9 is classified as Overweight, which
    increases the risk of conditions like high blood pressure, type 2 diabetes,
    and heart disease. A BMI of 30 or above falls into the Obesity category,
    which carries significantly higher health risks and is often associated
    with chronic conditions requiring medical management.
  </p>

  <h2 className="fw-bold mt-5 mb-3">Limitations of BMI</h2>
  <p>
    While BMI is a useful screening tool, it has important limitations that
    are worth understanding. BMI does not distinguish between muscle and fat —
    a highly muscular athlete may have a high BMI despite having very low body
    fat. It also does not account for where fat is distributed in the body,
    which matters a great deal for health risk. Belly fat (visceral fat) is
    more dangerous than fat stored in the hips and thighs, but BMI cannot
    detect this difference. Additionally, BMI thresholds were developed largely
    based on European populations and may not be equally accurate for people
    of Asian, African, or South Asian descent. For a complete health assessment,
    BMI should be considered alongside other measurements like waist
    circumference, blood pressure, cholesterol levels, and a doctor's evaluation.
  </p>

  <h2 className="fw-bold mt-5 mb-3">Healthy BMI Tips</h2>
  <p>
    If your BMI falls outside the normal range, there are practical steps you
    can take to move toward a healthier weight. For those who are overweight,
    a combination of a calorie-controlled diet and regular physical activity
    of at least 150 minutes per week is the most evidence-based approach.
    Even modest weight loss of 5–10% of body weight can significantly reduce
    health risks. For those who are underweight, increasing calorie intake
    through nutrient-dense foods and adding strength training to build muscle
    mass is generally recommended. Always consult a doctor or registered
    dietitian before making significant changes to your diet or exercise routine,
    especially if you have any underlying health conditions.
  </p>

  <h2 className="fw-bold mt-5 mb-3">Frequently Asked Questions</h2>

  <div className="mb-4">
    <h5>Is BMI an accurate measure of health?</h5>
    <p>
      BMI is a useful first-pass screening tool but is not a complete measure
      of health. It does not account for muscle mass, bone density, age, sex,
      or fat distribution. A muscular person may show as overweight on BMI
      despite being very fit, while someone with a normal BMI may still carry
      unhealthy levels of visceral fat. Use BMI as a starting point and consult
      a healthcare professional for a full assessment.
    </p>
  </div>

  <div className="mb-4">
    <h5>What is a healthy BMI for adults?</h5>
    <p>
      For most adults, a BMI between 18.5 and 24.9 is considered healthy. However,
      optimal ranges can vary slightly by age and ethnicity. For example, health
      authorities in several Asian countries use a lower threshold of 23 as the
      upper limit of normal weight, as studies have shown higher health risks at
      lower BMI values in Asian populations compared to Western populations.
    </p>
  </div>

  <div className="mb-4">
    <h5>What is a healthy BMI for children?</h5>
    <p>
      BMI for children and teenagers is interpreted differently from adults.
      Instead of fixed cutoff values, BMI in children is compared to growth
      charts based on age and sex. A pediatrician uses BMI-for-age percentiles
      to assess whether a child's weight is in a healthy range. Our calculator
      is designed for adults aged 18 and above.
    </p>
  </div>

  <div className="mb-4">
    <h5>Is this BMI calculator free?</h5>
    <p>
      Yes, our online BMI calculator is completely free to use with no
      registration required. It works on all devices including smartphones,
      tablets, and desktop computers, and gives you an instant result as soon
      as you enter your weight and height.
    </p>
  </div>

  {/* RELATED TOOLS */}
  <div className="mt-5 p-4 bg-light rounded-4">
    <h3 className="fw-bold mb-3">Related Tools</h3>
    <div className="d-flex flex-wrap gap-3">
      <a href="/percentage-calculator" className="btn btn-outline-primary">Percentage Calculator</a>
      <a href="/age-calculator" className="btn btn-outline-primary">Age Calculator</a>
      <a href="/emi-calculator" className="btn btn-outline-primary">EMI Calculator</a>
    </div>
  </div>

</div>

          </div>

        </div>

      </div>

      <Footer />
    </>
  );
}